import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, X, Maximize2, ClipboardCheck } from 'lucide-react';
import Footer from '../../components/Footer';
import { fadeInUp, staggerContainer, scaleIn } from '../../hooks/useScrollAnimation';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [isPanelMinimized, setIsPanelMinimized] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const autoplayRef = useRef(null);

  // After initial load animation completes, set isInitialLoad to false
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 5500); // Slightly after panel animation completes
    return () => clearTimeout(timer);
  }, []);

  // Function to close panel and disable initial load delay for future opens
  const handleClosePanel = () => {
    setIsPanelOpen(false);
    setIsInitialLoad(false); // Disable slow animation for future opens
  };

  const slides = [
    {
      id: 1,
      title: 'Racking',
      subtitle: 'Stability in every Rack',
      image: '/racking_aisle.jpg',
      link: '/products/racking',
    },
    {
      id: 2,
      title: 'Shelving',
      subtitle: 'Efficiency in every shelf',
      image: '/shelving_bay.jpg',
      link: '/products/shelving',
    },
    {
      id: 3,
      title: 'Mezzanine',
      subtitle: 'Maximize your vertical space',
      image: '/mezzanine_install.jpg',
      link: '/products/mezzanine',
    },
    {
      id: 4,
      title: 'Storage',
      subtitle: 'Organized and secure',
      image: '/lockers_storage.jpg',
      link: '/products/storage',
    },
  ];

  const capabilities = [
    { title: 'Site Survey', desc: 'We measure your space and assess your needs' },
    { title: 'CAD Design', desc: 'Custom layouts tailored to your requirements' },
    { title: 'Manufacturing', desc: 'Quality-controlled production in Johannesburg' },
    { title: 'Installation', desc: 'Professional teams ensuring safe setup' },
  ];

  // Auto-advance slides
  const startAutoplay = useCallback(() => {
    autoplayRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
  }, [slides.length]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
  }, []);

  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, [startAutoplay, stopAutoplay]);

  const goToSlide = (index) => {
    stopAutoplay();
    setCurrentSlide(index);
    startAutoplay();
  };

  const nextSlide = () => {
    stopAutoplay();
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    startAutoplay();
  };

  const prevSlide = () => {
    stopAutoplay();
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    startAutoplay();
  };

  // Animation variants
  const heroTextVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    })
  };

  const getPanelVariants = () => ({
    hidden: { x: 100, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { 
        duration: 0.5, 
        delay: isInitialLoad ? 4.789 : 0, 
        ease: [0.25, 0.1, 0.25, 1] 
      }
    },
    exit: {
      x: 100,
      opacity: 0,
      transition: { duration: 0.3 }
    }
  });

  return (
    <div className="relative">
      {/* Hero Section with Carousel */}
      <section className="relative w-full h-screen overflow-hidden bg-charcoal">
        {/* Carousel Slides */}
        <AnimatePresence mode="wait">
          {slides.map((slide, index) => (
            <motion.div
              key={slide.id}
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: index === currentSlide ? 1 : 0,
                scale: index === currentSlide ? 1 : 1.05
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
              className="absolute inset-0"
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={slide.image}
                  alt={slide.title}
                  width="1920"
                  height="1080"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-charcoal/50" />
              </div>

              {/* Center Content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <motion.h1
                  custom={0}
                  initial="hidden"
                  animate={index === currentSlide ? "visible" : "hidden"}
                  variants={heroTextVariants}
                  className="font-display font-bold text-white mb-4"
                  style={{
                    fontSize: 'clamp(48px, 8vw, 120px)',
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {slide.title}
                </motion.h1>
                <motion.p
                  custom={1}
                  initial="hidden"
                  animate={index === currentSlide ? "visible" : "hidden"}
                  variants={heroTextVariants}
                  className="text-white/80 mb-8 font-body"
                  style={{ fontSize: 'clamp(18px, 2.5vw, 32px)' }}
                >
                  {slide.subtitle}
                </motion.p>
                <motion.div
                  custom={2}
                  initial="hidden"
                  animate={index === currentSlide ? "visible" : "hidden"}
                  variants={heroTextVariants}
                  className="flex gap-4"
                >
                  <Link
                    to={slide.link}
                    className="bg-charcoal/80 backdrop-blur-sm text-white px-6 py-3 rounded-lg font-medium hover:bg-charcoal transition-all duration-300 hover:scale-105"
                  >
                    {slide.title}
                  </Link>
                  <Link
                    to="/contact"
                    className="bg-lime text-charcoal px-6 py-3 rounded-lg font-medium hover:bg-lime/90 transition-all duration-300 hover:scale-105"
                  >
                    Contact us
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          aria-label="Previous slide"
          className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 hover:scale-110 active:scale-95 transition-all duration-200 z-10"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          aria-label="Next slide"
          className={`absolute top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 hover:scale-110 active:scale-95 transition-all duration-200 z-10 ${
            isPanelOpen ? 'right-4 lg:right-[calc(34vw+2rem)]' : 'right-4 lg:right-8'
          }`}
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Dots Indicator */}
        <div 
          className="absolute bottom-8 left-1/2 flex gap-2 z-10 -translate-x-1/2"
        >
          {slides.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => goToSlide(index)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentSlide ? 'true' : 'false'}
              className={`h-1 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-lime' : 'bg-white/40 hover:bg-white/60'
              }`}
              style={{ width: index === currentSlide ? 48 : 32 }}
            />
          ))}
        </div>

        {/* Right Quote Panel */}
        <AnimatePresence>
          {isPanelOpen && (
            <motion.div
              variants={getPanelVariants()}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={`absolute right-0 top-0 h-full bg-offwhite shadow-panel hidden lg:flex flex-col z-30 ${
                isPanelMinimized ? 'w-auto' : 'w-full lg:w-[34vw]'
              }`}
            >
              {/* Panel Header with Controls */}
              <div className="flex items-center justify-between p-4 pt-20 lg:pt-24 border-b border-charcoal/10">
                <span className="font-mono text-base uppercase tracking-widest text-charcoal">
                  Get a quote
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleClosePanel}
                    className="w-8 h-8 rounded-lg bg-charcoal/5 flex items-center justify-center text-charcoal/60 hover:bg-charcoal/10 hover:text-charcoal transition-colors"
                    title="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Panel Content */}
              {!isPanelMinimized && (
                <div className="flex-1 flex items-center px-6 lg:px-10 py-8 overflow-y-auto pt-4">
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: isInitialLoad ? 4.789 : 0.2, duration: 0.5 }}
                    className="w-full"
                  >
                    <h2
                      className="font-display font-bold text-charcoal mb-6"
                      style={{ fontSize: 'clamp(24px, 2.5vw, 40px)', lineHeight: 1.1 }}
                    >
                      Industrial racking, shelving & mezzanine systems.
                    </h2>

                    <p
                      className="text-charcoal/70 font-body mb-8 leading-relaxed"
                      style={{ fontSize: 'clamp(14px, 1.1vw, 18px)' }}
                    >
                      We design, supply and install storage that fits your space, load and workflow—on time and on budget.
                    </p>

                    <Link
                      to="/products"
                      className="bg-lime text-charcoal px-6 py-3 rounded-lg font-medium flex items-center gap-2 hover:bg-lime/90 transition-all duration-300 hover:scale-105 mb-4 w-full justify-center"
                    >
                      Explore products
                      <ArrowRight className="w-4 h-4" />
                    </Link>

                    <Link
                      to="/contact"
                      className="text-charcoal/70 font-medium flex items-center gap-2 hover:text-charcoal transition-colors text-sm w-full justify-center"
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      Request a site survey
                    </Link>
                  </motion.div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Reopen Panel Button (when closed) */}
        {!isPanelOpen && (
          <motion.button
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            whileHover={{ scale: 1.05 }}
            onClick={() => {
              setIsPanelOpen(true);
              setIsPanelMinimized(false);
              setIsInitialLoad(false); // Ensure fast animation on manual open
            }}
            className="absolute right-4 top-28 bg-lime text-charcoal px-4 py-2 rounded-lg font-medium text-sm hover:bg-lime/90 transition-colors z-30 hidden lg:flex items-center gap-2 shadow-lg"
          >
            <Maximize2 className="w-4 h-4" />
            Get a quote
          </motion.button>
        )}
      </section>

      {/* Capabilities Section */}
      <section className="w-full bg-charcoal py-20 lg:py-32 overflow-hidden">
        <div className="px-6 lg:px-[7vw]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <h2
                className="font-display font-bold text-white mb-6"
                style={{ fontSize: 'clamp(32px, 4vw, 56px)', lineHeight: 1.1 }}
              >
                DESIGN.
                <br />
                SUPPLY.
                <br />
                INSTALL.
              </h2>
              <p className="text-white/80 mb-8 max-w-lg">
                From first measurement to final bolt, we handle the full lifecycle—so your team can stay focused on operations.
              </p>
              <Link
                to="/about"
                className="bg-lime text-charcoal px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2 hover:bg-lime/90 transition-all duration-300 hover:scale-105"
              >
                About Us
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid grid-cols-2 gap-4"
            >
              {capabilities.map((cap, index) => (
                <motion.div
                  key={index}
                  variants={scaleIn}
                  whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-6 transition-colors hover:bg-white/10"
                >
                  <h3 className="font-display font-bold text-white text-lg mb-2">{cap.title}</h3>
                  <p className="text-white/50 text-sm">{cap.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="w-full bg-offwhite py-20 lg:py-32">
        <div className="px-6 lg:px-[7vw]">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2
              className="font-display font-bold text-charcoal mb-4"
              style={{ fontSize: 'clamp(32px, 4vw, 56px)', lineHeight: 1.1 }}
            >
              Our Products
            </h2>
            <p className="text-charcoal/60 max-w-xl mx-auto">
              Industrial-grade storage solutions designed for warehouses, retail, and manufacturing facilities.
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
          >
            {slides.map((product, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <Link
                  to={product.link}
                  className="group relative h-80 lg:h-96 rounded-2xl overflow-hidden block"
                >
                  <img
                    src={product.image}
                    alt={product.title}
                    width="800"
                    height="600"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                    <h3 className="font-display font-bold text-white text-2xl lg:text-3xl mb-2 group-hover:text-lime transition-colors duration-300">
                      {product.title}
                    </h3>
                    <p className="text-white/70">{product.subtitle}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-center mt-12"
          >
            <Link
              to="/products"
              className="bg-charcoal text-white px-8 py-4 rounded-lg font-medium inline-flex items-center gap-2 hover:bg-charcoal/80 transition-all duration-300 hover:scale-105"
            >
              View All Products
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full bg-charcoal py-20 lg:py-32 overflow-hidden">
        <div className="px-6 lg:px-[7vw] text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="font-display font-bold text-white mb-4"
              style={{ fontSize: 'clamp(32px, 4vw, 56px)', lineHeight: 1.1 }}
            >
              Ready to optimize your space?
            </h2>
            <p className="text-white/80 max-w-xl mx-auto mb-8">
              Get a free site survey and quote. We'll help you find the perfect storage solution for your business.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/contact"
                  className="bg-lime text-charcoal px-8 py-4 rounded-lg font-medium hover:bg-lime/90 transition-colors inline-block"
                >
                  Request a Quote
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a
                  href="tel:+27829713299"
                  className="bg-white/10 text-white px-8 py-4 rounded-lg font-medium hover:bg-white/20 transition-colors inline-block"
                >
                  Call Us
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;
